// Unified Feedback Inbox — the inbox *frame*: layout, URL-driven data
// fetching, RBAC gating. The composer slot (PR 06) is still a later chassis
// PR; list, thread, and rail (incl. look-back) are real as of this PR.
//
// Cross-scene mount exception (docs/05-frontend-components.md
// "Architecture — decided", 11-chassis-plan.md §0 "Location"): this
// component lives under its own `src/scenes/FeedbackInbox/` folder but is
// mounted from TWO other scenes — `scenes/OrderFeedback`'s "Feedback" tab
// and `RecoveryAI/Landing`'s "Escalations" tab (both land in PR
// 08) — rather than being promoted to `src/components`. The repo's usual
// "never import across scene boundaries; shared components go in
// src/components" rule assumes a shared component is presentational;
// `FeedbackInbox` owns real data-fetching and URL state, which doesn't fit
// that mold. This is an explicitly accepted, one-off exception for this
// feature — not a precedent for other scene-to-scene imports.
import {
  useGetFeedbackInboxCountsQuery,
  useGetFeedbackInboxItemQuery,
  useGetFeedbackInboxQuery,
  useGetInboxLocationsQuery,
} from 'api/feedbackInbox'
import type { FeedbackInboxDetail, SetupState } from 'api/feedbackInbox.types'
import ConfirmModal from 'components/ConfirmModal'
import type { PageSize } from 'components/TablePaginationFooter'
import type { History } from 'history'
import { buildTranslate } from 'locales'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { history as defaultHistory } from 'store'
import { useAbility } from 'utilities/ability'
import InboxComposer from './InboxComposer'
import InboxFilterBar, { type InboxFilterBarHandle } from './InboxFilterBar'
import InboxLayout from './InboxLayout'
import InboxList from './InboxList'
import LiveRegion from './LiveRegion'
import RightRail, { type RailTab } from './RightRail'
import ThreadPane from './ThreadPane'
import type { UseInboxActionsResult } from './ThreadPane/useInboxActions'
import { useDiscardGuard } from './useDiscardGuard'
import type { InboxFilterPatch, InboxInitialParams } from './useInboxParams'
import { isNoopFilterPatch, useInboxParams } from './useInboxParams'
import { useLiveAnnouncer } from './useLiveAnnouncer'
import { useResolveAutoAdvance } from './useResolveAutoAdvance'

const t = buildTranslate('feedback_inbox')

export interface ViewerCan {
  manage: boolean
  grant: boolean
}

// `manage Feedback` gates resolve/unresolve (restrict is `manage
// MerchantSettings`, the server's gate — RightRail/useCustomerRestriction);
// `manage RewardIssuance` additionally gates the composer's reward/points
// picker (docs/06-ux-rules.md "View vs manage"). Exported for its own unit
// test, mirroring `resolveKind`/`deriveResolutionType`'s "pure derivation,
// tested directly" pattern elsewhere in this feature.
export function deriveViewerCan(ability: {
  can: (action: string, subject: string) => boolean
}): ViewerCan {
  return {
    manage: ability.can('manage', 'Feedback'),
    grant: ability.can('manage', 'RewardIssuance'),
  }
}

// Exported (not just `useViewerCan` below) so PR 05's `ThreadPane` stories
// can mount in isolation — inside `InboxLayout` but without the full
// `FeedbackInbox` container — by supplying their own
// `ViewerCanContext.Provider`, the same way `InboxList`'s own PR 04 stories
// render presentationally rather than through the full container.
export const ViewerCanContext = createContext<ViewerCan | null>(null)

// Exposed via context (not prop-drilled through InboxLayout's slots) so
// PR 05/06's ThreadHeader/InboxComposer can read it directly — mirrors
// InboxLayout's `useInboxRailControls` context for the same reason.
export function useViewerCan(): ViewerCan {
  const context = useContext(ViewerCanContext)
  if (!context) {
    throw new Error('useViewerCan must be used within FeedbackInbox')
  }
  return context
}

export interface FeedbackInboxProps {
  initialParams: InboxInitialParams
  setupState: SetupState
  // Accepted for the documented mount API (docs/05-frontend-components.md,
  // 11-chassis-plan.md PR 02: `<FeedbackInbox initialParams setupState
  // collecting />`). Unread inside this component, and no longer a host-side
  // gate either: `isOrderFeedbackTabVisible` ignores it (ISS-4285).
  collecting: boolean
  // Merchant's written-feedback collection setting (FR-23). False hides the
  // Order Feedback kind segment (it lists only commented thumbs-downs) and
  // resolves kind=feedback to All. Defaults to true.
  writtenFeedbackEnabled?: boolean
  // Test/story seam only — see `useInboxParams.ts`'s `historyOverride` doc
  // comment for why the real singleton `history` isn't safe to drive from
  // Storybook. Never passed by the two real mounts.
  history?: History
}

const FeedbackInbox = (props: FeedbackInboxProps) => {
  const {
    initialParams,
    setupState,
    history,
    writtenFeedbackEnabled = true,
  } = props
  const {
    params,
    setFilter,
    setSearch,
    setPage,
    selectItem,
    openLookback,
    closeLookback,
  } = useInboxParams(initialParams, setupState, history, writtenFeedbackEnabled)
  const ability = useAbility()
  const viewerCan = useMemo(() => deriveViewerCan(ability), [ability])
  const filterBarRef = useRef<InboxFilterBarHandle>(null)
  const { message, announce } = useLiveAnnouncer()
  // Composer discard-confirm (docs/06-ux-rules.md "Composer" / "Discard-
  // confirm"; P6 D10) — intercepts a selection change BEFORE it happens, so
  // "Keep editing" can leave the ORIGINAL item selected. See
  // `useDiscardGuard`'s own header comment for why this has to live here
  // rather than reacting to `detail.id` after the fact inside the composer.
  const discardGuard = useDiscardGuard()

  // Session-only rail tab selection (P8 D1) — lives here, not in `RightRail`
  // itself, so it survives an item switch (a `RightRail` remount would
  // otherwise reset it to its own default every time `itemId` changes).
  const [railTab, setRailTab] = useState<RailTab>('customer')

  // `useInboxParams.closeLookback()` always PUSHES a fresh URL without
  // `lookback=` — it does not know whether THIS particular look-back was
  // reached by pushing (a Recent-feedback row click, in-session) or was
  // already in the URL when the page loaded (a deep link, or a look-back
  // that survived earlier navigation) — docs/10-risks-and-followups.md
  // (FE-7 note). This ref is the container's own record of "did WE push the
  // currently-open look-back", so Back can `history.goBack()` (popping back
  // to the pre-look-back list state, browser-Back-Forward-cache-friendly)
  // instead of always pushing a THIRD entry. Same `history` instance
  // `useInboxParams` itself defaults to when the mount doesn't pass one.
  const activeHistory = history ?? defaultHistory
  const lookbackPushedRef = useRef(false)

  // Browser Back popping `lookback=` off the URL (or `closeLookback`'s own
  // push doing the same) both land here with `params.lookback` now falsy —
  // either way, there's no more "our push" left to unwind.
  useEffect(() => {
    if (!params.lookback) {
      lookbackPushedRef.current = false
    }
  }, [params.lookback])

  // Guarded like `handleSelectItem` below: opening a look-back changes
  // `activeDetailId` → `ThreadPane`'s `itemId` → `useInboxComposerDraft`'s
  // `detail.id`, and that hook's reset effect assumes the container already
  // confirmed the discard. Unguarded, a Recent-feedback click silently
  // threw away an unsent reply with no way to recover it.
  const handleOpenLookback = useCallback(
    (id: string) =>
      discardGuard.confirmDiscard(() => {
        lookbackPushedRef.current = true
        openLookback(id)
        announce(t('lookback.opened_announcement'))
      }),
    [discardGuard, openLookback, announce]
  )

  // Back is guarded like open (S4 §3.2 G3): leaving swaps `activeDetailId`
  // back, which resets the look-back composer's draft. Browser Back is not;
  // a dialog can't intercept popstate without `history.block` (G4, v1
  // follow-up).
  const handleCloseLookback = useCallback(
    () =>
      discardGuard.confirmDiscard(() => {
        if (lookbackPushedRef.current) {
          lookbackPushedRef.current = false
          activeHistory.goBack()
        } else {
          closeLookback()
        }
        announce(t('lookback.closed_announcement'))
      }),
    [discardGuard, activeHistory, closeLookback, announce]
  )

  const listQuery = useGetFeedbackInboxQuery({
    kind: params.kind,
    status: params.status,
    rating: params.rating ?? undefined,
    location_ids: params.locationIds,
    search: params.q,
    page: params.page,
    per_page: params.perPage,
  })
  const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data])

  const { data: counts } = useGetFeedbackInboxCountsQuery({
    status: params.status,
    location_ids: params.locationIds,
    rating: params.rating ?? undefined,
  })

  const { data: locations } = useGetInboxLocationsQuery()

  // The detail query follows a look-back target over the live selection
  // when both are set (docs/06-ux-rules.md "Look-back") — but the LIST
  // pane's own "selected row" highlighting always tracks the live `item`
  // param, never the look-back target (the list is hidden entirely behind
  // `LookbackBackHeader` while a look-back is open). `ThreadPane` (below)
  // owns the actual `useGetFeedbackInboxItemQuery` call now that PR 05
  // fills in the thread pane — this id is just threaded through as its
  // `itemId` prop.
  const activeDetailId = params.lookback ?? params.item
  // Same args as ThreadPane's query, so RTK dedupes to one request; read here
  // only for the past order's date (left-column copy). `currentData`, not
  // `data`: `data` keeps the previous look-back's result (RTK 1.9.5).
  const { currentData: lookbackDetail } = useGetFeedbackInboxItemQuery(
    params.lookback ?? '',
    { skip: !params.lookback }
  )

  // "If the page empties, refetch and select first" (docs/06-ux-rules.md
  // "Auto-advance"). The first SETTLED page-1 result after `onPageEmptied`
  // consumes the flag, even an empty one, so a later unresolve or filter
  // change can't announce "Resolved — showing …" (AI-1408).
  // context:
  //   State, not a ref, so a refetch that landed before the exit ended
  //   still gets a pass. `isFetching` is RTK's in-flight flag for the
  //   CURRENT args and is true while they're uninitialized (after setPage).
  const [selectFirstPending, setSelectFirstPending] = useState(false)
  const listSettled = !listQuery.isFetching
  useEffect(() => {
    if (!selectFirstPending || !listSettled || params.page !== 1) return
    setSelectFirstPending(false)
    const first = items[0]
    if (!first) return
    selectItem(first.id)
    announce(
      t('actions.resolved_announcement', {
        name: first.customer.name ?? t('list.unknown_guest'),
      })
    )
  }, [
    selectFirstPending,
    listSettled,
    params.page,
    items,
    selectItem,
    announce,
  ])

  const onPageEmptied = useCallback(() => {
    setSelectFirstPending(true)
    if (params.page !== 1) setPage(1)
  }, [params.page, setPage])

  const pulseResolvedCount = useCallback(() => {
    filterBarRef.current?.pulseResolvedCount()
  }, [])

  const { displayItems, getAnimationPhase, confirmResolved, rollbackResolved } =
    useResolveAutoAdvance({
      items,
      statusFilter: params.status,
      selectedId: params.item,
      onSelect: selectItem,
      onPageEmptied,
      announce,
      pulseResolvedCount,
      lookbackId: params.lookback,
      awaitResolveConfirmation: true,
    })

  // PR 01's keep-item rule ("keep `item` if still visible after a search
  // change, else clear it") was stubbed until the list pane existed to
  // supply what "visible" means — wired here against the CURRENT page's ids
  // (the new page's ids aren't known synchronously; this mirrors the only
  // data available at the moment the search actually changes).
  const visibleItemIds = useMemo(() => items.map(item => item.id), [items])

  // A filter bar change always deselects the item (`setFilter`), so it is
  // guarded like `handleSelectItem` below: unguarded, it would silently
  // throw away an unsent reply. A click on the segment that is already
  // active changes nothing, so it skips the guard. The new list must not
  // auto-advance onto its first row, so the change also cancels a pending
  // page-emptied select.
  const handleFilterChange = useCallback(
    (patch: InboxFilterPatch) => {
      if (
        isNoopFilterPatch(params, patch, setupState, writtenFeedbackEnabled)
      ) {
        return
      }
      discardGuard.confirmDiscard(() => {
        setSelectFirstPending(false)
        setFilter(patch)
      })
    },
    [discardGuard, params, setFilter, setupState, writtenFeedbackEnabled]
  )

  const handleSearchChange = useCallback(
    (q: string) => setSearch(q, { visibleItemIds }),
    [setSearch, visibleItemIds]
  )

  const handlePageChange = useCallback(
    (page: number) => setPage(page),
    [setPage]
  )

  const handlePageSizeChange = useCallback(
    (size: PageSize) => setPage(1, size),
    [setPage]
  )

  // Success-only (`useInboxActions` calls it after `unwrap()`): releases the
  // auto-advance that `awaitResolveConfirmation` held. A look-back target
  // never advances (S4 §3.2 G1/G2/G5): announce plain "Resolved" and leave
  // the look-back open.
  const handleResolved = useCallback(
    (id: string) => {
      confirmResolved(id)
      if (id === params.lookback) {
        announce(t('actions.resolved_lookback_announcement'))
      }
    },
    [confirmResolved, params.lookback, announce]
  )

  // Failure-only counterpart, from the mutation's own catch path
  // (`useInboxActions`) — the ONLY thing gated mode treats as a genuine
  // rollback, since a bare resolved→open transition can also be a stale
  // refetch racing an in-flight resolve of the same row (review thread,
  // useResolveAutoAdvance.ts).
  const handleResolveFailed = useCallback(
    (id: string) => rollbackResolved(id),
    [rollbackResolved]
  )

  // The LIST's own selection change is the one path the discard-confirm
  // guard wraps (docs/06-ux-rules.md "Composer" / "Discard-confirm") —
  // `useResolveAutoAdvance`'s internal advance (above) runs only right
  // after a successful send, which has already cleared the composer's
  // draft, so it calls `selectItem` directly rather than through the guard.
  const handleSelectItem = useCallback(
    (id: string) => discardGuard.confirmDiscard(() => selectItem(id)),
    [discardGuard, selectItem]
  )

  const selectedCustomerName =
    items.find(item => item.id === params.item)?.customer.name ?? null

  const renderComposer = useCallback(
    (detail: FeedbackInboxDetail, actions: UseInboxActionsResult) => (
      <InboxComposer
        detail={detail}
        actions={actions}
        viewerCan={viewerCan}
        onDirtyChange={discardGuard.setDirty}
      />
    ),
    [viewerCan, discardGuard]
  )

  return (
    <ViewerCanContext.Provider value={viewerCan}>
      <InboxLayout>
        <InboxLayout.FilterBar>
          <InboxFilterBar
            ref={filterBarRef}
            status={params.status}
            kind={params.kind}
            rating={params.rating}
            locationIds={params.locationIds}
            setupState={setupState}
            writtenFeedbackEnabled={writtenFeedbackEnabled}
            counts={counts}
            locations={locations ?? []}
            onFilterChange={handleFilterChange}
          />
        </InboxLayout.FilterBar>
        <InboxLayout.List>
          <InboxList
            status={params.status}
            kind={params.kind}
            rating={params.rating}
            q={params.q}
            onSearchChange={handleSearchChange}
            locationIds={params.locationIds}
            items={displayItems}
            totalCount={listQuery.data?.meta.totalCount ?? 0}
            totalPages={listQuery.data?.meta.totalPages ?? 1}
            page={params.page}
            pageSize={params.perPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isLoading={listQuery.isLoading}
            isError={listQuery.isError}
            onRetry={listQuery.refetch}
            selectedId={params.item}
            onSelect={handleSelectItem}
            getAnimationPhase={getAnimationPhase}
            lookback={params.lookback}
            lookbackDate={
              lookbackDetail?.order?.orderDate ?? lookbackDetail?.date
            }
            onCloseLookback={handleCloseLookback}
          />
        </InboxLayout.List>
        <InboxLayout.Thread>
          <ThreadPane
            itemId={activeDetailId ?? null}
            isLookback={!!params.lookback}
            onResolved={handleResolved}
            onResolveFailed={handleResolveFailed}
            announce={announce}
            renderComposer={renderComposer}
            beforeResolve={discardGuard.confirmDiscard}
          />
        </InboxLayout.Thread>
        <InboxLayout.Rail>
          <RightRail
            itemId={activeDetailId ?? null}
            mode={params.lookback ? 'lookback' : 'live'}
            activeTab={railTab}
            onTabChange={setRailTab}
            onOpenLookback={handleOpenLookback}
            announce={announce}
            beforeRestrict={discardGuard.confirmDiscard}
          />
        </InboxLayout.Rail>
      </InboxLayout>
      <LiveRegion message={message} />
      <ConfirmModal
        isOpen={discardGuard.isOpen}
        isJustified
        title={t('composer.discard_confirm_title', {
          name: selectedCustomerName ?? t('list.unknown_guest'),
        })}
        description={t('composer.discard_confirm_body')}
        confirmText={t('composer.discard_confirm_cta')}
        cancelText={t('composer.discard_cancel_cta')}
        onConfirm={discardGuard.discard}
        onClose={discardGuard.keepEditing}
      />
    </ViewerCanContext.Provider>
  )
}

export default FeedbackInbox
